import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  Alert,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedEntityLink } from "../ThemedEntityLink";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedChip } from "../ThemedChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { extractAnchorCoordinates, type IPopoverAnchor } from "@/utils/popover-positioning";
import { linkEntity, unlinkEntity } from "@/services/firebase/entity-link-functions";
import { getMedia } from "@/services/firebase/media-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { getMemory } from "@/services/firebase/memory-functions";
import { MemorySelector } from "../memory/MemorySelector";
import { MediaSelector } from "../media/MediaSelector";
import { ContactSelector } from "../contacts/ContactSelector";
import { GroupSelector } from "../groups/GroupSelector";
import { PlaceSelector } from "../places/PlaceSelector";
import { AnchorSelector } from "../anchors/AnchorSelector";
import { getMyPlaces } from "@/services/firebase/place-functions";
import { getMyAnchors } from "@/services/firebase/anchor-functions";
import { useFirebase } from "../firebase/useFirebase";
import type { ContactModel, GroupModel, MemoryModel, MediaModel, PlaceModel, AnchorModel } from "common247";
import { getChipVisualConfig } from "@/constants/themes/components/chip-theme";

export type EntityLinkType = "memory" | "media" | "contact" | "group" | "place" | "anchor";

export interface IEntityLinkStripProps {
  /** The source entity document ID (empty if creating new item) */
  entityId?: string;
  /** The source entity type */
  entityType: EntityLinkType;
  /** The target entity type to display / link */
  targetType: EntityLinkType;
  /** Foreign key relationship map of linked entities (e.g. Record<targetId, IEntity*Meta>) */
  links?: Record<string, any>;
  /** Whether the user has permission to link / unlink */
  canEdit?: boolean;
  /** Whether the current user is the owner */
  isOwner?: boolean;
  /** Current user UID for permissions */
  currentUserId?: string;
  /** Compact style */
  compact?: boolean;
  /** Custom container style */
  style?: StyleProp<ViewStyle>;
  /** Custom button label override (default: "+ Memory", "+ Media", etc.) */
  buttonLabel?: string;
  /** Whether the selector allows selecting multiple items at once (default: true for media) */
  multiSelect?: boolean;
  /** Callback fired when a link is added */
  onLinkAdded?: (meta: any) => void;
  /** Callback fired when a link is removed */
  onLinkRemoved?: (targetId: string) => void;
  /** Callback fired with the updated links map */
  onLinksChange?: (links: Record<string, any>) => void;
  /** Optional pre-resolved titles dictionary keyed by entity ID */
  titlesById?: Record<string, string>;
  /** Optional pre-resolved thumbnails dictionary keyed by entity ID (e.g. for media) */
  thumbnailsById?: Record<string, string>;
}

const EMPTY_LINK_MAP: Record<string, any> = Object.freeze({});

/**
 * Returns visual color tokens and icons for each entity link type.
 * Strictly adheres to the Color Ban: Uses Emerald, Blue, Rose, Amber.
 */
function getEntityLinkVisualConfig(type: EntityLinkType, isDark: boolean) {
  switch (type) {
    case "contact":
      return {
        icon: "person-circle-outline" as const,
        borderColor: isDark ? "rgba(52, 211, 153, 0.45)" : "rgba(5, 150, 105, 0.35)",
        bgColor: isDark ? "rgba(52, 211, 153, 0.14)" : "rgba(5, 150, 105, 0.09)",
        textColor: isDark ? "#34D399" : "#047857",
        label: "Contact",
      };
    case "group":
      return {
        icon: "people-outline" as const,
        borderColor: isDark ? "rgba(230, 224, 168, 0.55)" : "#C5B866",
        bgColor: isDark ? "rgba(230, 224, 168, 0.20)" : "#F7F2C8",
        textColor: isDark ? "#E6E0A8" : "#6B5E00",
        label: "Group",
      };
    case "memory":
      return {
        icon: "sparkles-outline" as const,
        borderColor: isDark ? "rgba(244, 63, 94, 0.45)" : "rgba(225, 29, 72, 0.35)",
        bgColor: isDark ? "rgba(244, 63, 94, 0.14)" : "rgba(225, 29, 72, 0.09)",
        textColor: isDark ? "#FB7185" : "#BE123C",
        label: "Memory",
      };
    case "media":
      return {
        icon: "images-outline" as const,
        borderColor: isDark ? "rgba(251, 191, 36, 0.45)" : "rgba(217, 119, 6, 0.35)",
        bgColor: isDark ? "rgba(251, 191, 36, 0.14)" : "rgba(217, 119, 6, 0.09)",
        textColor: isDark ? "#FBBF24" : "#B45309",
        label: "Media",
      };
    case "place": {
      const visual = getChipVisualConfig("place", isDark);
      return {
        icon: "location-outline" as const,
        borderColor: visual.borderColor,
        bgColor: visual.badgeBg,
        textColor: visual.textColor,
        label: "Place",
      };
    }
    case "anchor": {
      const visual = getChipVisualConfig("anchor", isDark);
      return {
        icon: "boat-outline" as const,
        borderColor: visual.borderColor,
        bgColor: visual.badgeBg,
        textColor: visual.textColor,
        label: "Anchor",
      };
    }
  }
}

/**
 * Standardized inline relationship strip for linking Contacts, Groups, Memories, and Media.
 * Renders associated items as interactive ThemedEntityLinks with permissions-aware removal
 * and a contextual "+ [Entity]" selector popover.
 */
export function EntityLinkStrip({
  entityId = "",
  entityType,
  targetType,
  links,
  canEdit = false,
  isOwner = false,
  currentUserId,
  compact = false,
  style,
  buttonLabel,
  multiSelect = true,
  onLinkAdded,
  onLinkRemoved,
  onLinksChange,
  titlesById = {},
  thumbnailsById = {},
}: IEntityLinkStripProps) {
  const services = useFirebase();
  const [modalOpen, setModalOpen] = useState(false);
  const [anchor, setAnchor] = useState<IPopoverAnchor | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Local title cache populated when items are selected or passed via props
  const [localTitles, setLocalTitles] = useState<Record<string, string>>(titlesById);

  useEffect(() => {
    if (titlesById && Object.keys(titlesById).length > 0) {
      setLocalTitles((prev) => ({ ...prev, ...titlesById }));
    }
  }, [titlesById]);

  // Local thumbnail cache populated when items are selected or passed via props
  const [localThumbnails, setLocalThumbnails] = useState<Record<string, string>>(thumbnailsById);

  useEffect(() => {
    if (thumbnailsById && Object.keys(thumbnailsById).length > 0) {
      setLocalThumbnails((prev) => ({ ...prev, ...thumbnailsById }));
    }
  }, [thumbnailsById]);

  // Normalize links input: handles Record<string, any>, arrays, or null/undefined
  const normalizedInitialLinks = useMemo(() => {
    if (!links) return EMPTY_LINK_MAP;
    if (Array.isArray(links)) {
      if (links.length === 0) return EMPTY_LINK_MAP;
      const rec: Record<string, any> = {};
      links.forEach((item, idx) => {
        if (typeof item === "string") {
          rec[item] = { id: item, addedAt: Date.now(), addedBy: "system", source: "manual" };
        } else if (item && typeof item === "object") {
          const id = item.id || item.memoryId || item.mediaId || item.contactId || item.groupId || `item_${idx}`;
          rec[id] = item;
        }
      });
      return rec;
    }
    if (typeof links === "object") {
      if (Object.keys(links).length === 0) return EMPTY_LINK_MAP;
      return links;
    }
    return EMPTY_LINK_MAP;
  }, [links]);

  const [localLinks, setLocalLinks] = useState<Record<string, any>>(normalizedInitialLinks);

  // Sync when link keys or counts change
  useEffect(() => {
    setLocalLinks((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(normalizedInitialLinks || {});
      if (
        prevKeys.length === nextKeys.length &&
        prevKeys.every((k) => k in normalizedInitialLinks)
      ) {
        return prev;
      }
      return normalizedInitialLinks;
    });
  }, [normalizedInitialLinks]);

  const isDark = useThemeColor({}, "card") === "#131520";
  const visualConfig = getEntityLinkVisualConfig(targetType, isDark);
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");

  const linkEntries = useMemo(() => {
    return Object.entries(localLinks || {}).map(([key, meta]) => {
      const id = meta?.id || meta?.memoryId || meta?.mediaId || meta?.contactId || meta?.groupId || key;
      const rawMetaTitle = meta?.displayName || meta?.title || meta?.name;
      const hasRealMetaName = Boolean(
        rawMetaTitle &&
        !["contact", "group", "memory", "media"].includes(rawMetaTitle.trim().toLowerCase())
      );
      const resolvedFromMeta = hasRealMetaName ? rawMetaTitle : undefined;

      const title =
        resolvedFromMeta ||
        localTitles[id] ||
        (meta?.source === "description" ? `${visualConfig.label} (from description)` : `${visualConfig.label}`);
      return {
        id,
        meta: typeof meta === "object" ? meta : { id },
        title,
        source: meta?.source || "manual",
        addedBy: meta?.addedBy || "",
      };
    });
  }, [localLinks, localTitles, visualConfig.label]);

  // Pre-fetch missing entity metadata (thumbnail & title) across media, contacts, groups, and memories
  useEffect(() => {
    if (!services) return;
    const missingIds = linkEntries
      .map((entry) => entry.id)
      .filter((id) => id && !localTitles[id]);

    if (missingIds.length === 0 && (targetType !== "media" || linkEntries.every((e) => !e.id || localThumbnails[e.id]))) {
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        if (targetType === "media") {
          for (const id of linkEntries.map((e) => e.id)) {
            if (!id || (localThumbnails[id] && localTitles[id])) continue;
            try {
              const res = await getMedia(services, { mediaId: id });
              if (isMounted && res?.media) {
                const m = res.media;
                const thumb = m.thumbnailUrl || m.url || m.downloadUrl || "";
                const t = m.title || (m as any).name || "";
                if (thumb) {
                  setLocalThumbnails((prev) => ({ ...prev, [id]: thumb }));
                }
                if (t) {
                  setLocalTitles((prev) => ({ ...prev, [id]: t }));
                }
              }
            } catch {
              // Graceful fallback: ThemedCompactCard renders default placeholder icon
            }
          }
        } else if (targetType === "contact") {
          const res = await getMyContacts(services, { includeInactive: false });
          if (isMounted && res?.contacts) {
            const newTitles: Record<string, string> = {};
            for (const c of res.contacts) {
              const cId = c.id || (c as any).contactId;
              const name = c.displayName || (c as any).fullName || (c as any).name || c.email;
              if (cId && name) {
                newTitles[cId] = name;
              }
            }
            if (Object.keys(newTitles).length > 0) {
              setLocalTitles((prev) => ({ ...prev, ...newTitles }));
            }
          }
        } else if (targetType === "group") {
          const res = await getMyGroups(services, {});
          if (isMounted && res?.groups) {
            const newTitles: Record<string, string> = {};
            for (const g of res.groups) {
              const gId = g.id || (g as any).groupId;
              const name = g.name || (g as any).displayName;
              if (gId && name) {
                newTitles[gId] = name;
              }
            }
            if (Object.keys(newTitles).length > 0) {
              setLocalTitles((prev) => ({ ...prev, ...newTitles }));
            }
          }
        } else if (targetType === "memory") {
          for (const id of missingIds) {
            try {
              const res = await getMemory(services, { memoryId: id });
              if (isMounted && res?.memory?.title) {
                setLocalTitles((prev) => ({ ...prev, [id]: res.memory.title }));
              }
            } catch {
              // Graceful fallback
            }
          }
        } else if (targetType === "place") {
          const res = await getMyPlaces(services, {});
          if (isMounted && res?.places) {
            const newTitles: Record<string, string> = {};
            for (const p of res.places) {
              const pId = p.id;
              const name = p.name || (p as any).displayName;
              if (pId && name) {
                newTitles[pId] = name;
              }
            }
            if (Object.keys(newTitles).length > 0) {
              setLocalTitles((prev) => ({ ...prev, ...newTitles }));
            }
          }
        } else if (targetType === "anchor") {
          const res = await getMyAnchors(services, {});
          if (isMounted && res?.anchors) {
            const newTitles: Record<string, string> = {};
            for (const a of res.anchors) {
              const aId = a.id;
              const name = a.name;
              if (aId && name) {
                newTitles[aId] = name;
              }
            }
            if (Object.keys(newTitles).length > 0) {
              setLocalTitles((prev) => ({ ...prev, ...newTitles }));
            }
          }
        }
      } catch (err) {
        console.warn(`[EntityLinkStrip] Auto-resolution error for ${targetType}:`, err);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [targetType, linkEntries, services, localThumbnails, localTitles]);

  const handleRemoveLink = async (targetId: string, meta: any) => {
    if (!services || removingId) return;

    const performRemoval = async () => {
      setRemovingId(targetId);
      try {
        if (entityId) {
          const res = await unlinkEntity(services, {
            sourceType: entityType,
            sourceId: entityId,
            targetType: targetType,
            targetId: targetId,
          });

          if (!res.status) {
            const msg = res.error || `Failed to unlink ${visualConfig.label.toLowerCase()}.`;
            if (Platform.OS === "web") {
              window.alert(msg);
            } else {
              Alert.alert("Error", msg);
            }
            return;
          }
        }

        const updated = { ...localLinks };
        delete updated[targetId];
        setLocalLinks(updated);
        onLinkRemoved?.(targetId);
        onLinksChange?.(updated);
      } catch (err: any) {
        console.error(`[EntityLinkStrip] Error unlinking ${targetType}:`, err);
        const msg = err.message || `Failed to unlink ${visualConfig.label.toLowerCase()}.`;
        if (Platform.OS === "web") {
          window.alert(msg);
        } else {
          Alert.alert("Error", msg);
        }
      } finally {
        setRemovingId(null);
      }
    };

    if (Platform.OS === "web") {
      await performRemoval();
    } else {
      Alert.alert(
        `Unlink ${visualConfig.label}`,
        `Unlink this ${visualConfig.label.toLowerCase()} from this ${entityType}?`,
        [
          { text: "Cancel", style: "cancel" },
          { text: "Unlink", style: "destructive", onPress: performRemoval },
        ]
      );
    }
  };

  const handleEntitySelected = async (targetId: string, displayTitle: string, thumbnailUrl?: string) => {
    setModalOpen(false);
    if (!targetId) return;

    // Cache the title and thumbnail for instant display
    if (displayTitle) {
      setLocalTitles((prev) => ({ ...prev, [targetId]: displayTitle }));
    }
    if (thumbnailUrl) {
      setLocalThumbnails((prev) => ({ ...prev, [targetId]: thumbnailUrl }));
    }

    const newMeta: any = {
      addedAt: Date.now(),
      addedBy: currentUserId || services?.auth?.currentUser?.uid || "",
      source: "manual",
      displayName: displayTitle,
    };
    if (targetType === "memory") newMeta.memoryId = targetId;
    if (targetType === "media") {
      newMeta.mediaId = targetId;
      if (thumbnailUrl) newMeta.thumbnailUrl = thumbnailUrl;
    }
    if (targetType === "contact") newMeta.contactId = targetId;
    if (targetType === "group") newMeta.groupId = targetId;
    if (targetType === "place") newMeta.placeId = targetId;
    if (targetType === "anchor") newMeta.anchorId = targetId;

    if (entityId && services) {
      try {
        const res = await linkEntity(services, {
          sourceType: entityType,
          sourceId: entityId,
          targetType: targetType,
          targetId: targetId,
          source: "manual",
        });

        if (!res.status) {
          const msg = res.error || `Failed to link ${visualConfig.label.toLowerCase()}.`;
          if (Platform.OS === "web") {
            window.alert(msg);
          } else {
            Alert.alert("Error", msg);
          }
          return;
        }
      } catch (err: any) {
        console.error(`[EntityLinkStrip] Error linking ${targetType}:`, err);
        const msg = err.message || `Failed to link ${visualConfig.label.toLowerCase()}.`;
        if (Platform.OS === "web") {
          window.alert(msg);
        } else {
          Alert.alert("Error", msg);
        }
        return;
      }
    }

    const updated = {
      ...(localLinks || {}),
      [targetId]: newMeta,
    };
    setLocalLinks(updated);
    onLinkAdded?.(newMeta);
    onLinksChange?.(updated);
  };

  /**
   * Links multiple selected entities (e.g. from multi-select MediaSelector).
   */
  const handleMultipleEntitiesSelected = async (
    items: Array<{ id: string; title: string; thumbnailUrl?: string }>
  ) => {
    setModalOpen(false);
    if (!items || items.length === 0) return;

    const newTitles: Record<string, string> = {};
    const newThumbnails: Record<string, string> = {};
    const updated = { ...(localLinks || {}) };

    for (const item of items) {
      if (!item.id) continue;
      const displayTitle = item.title || item.id;
      newTitles[item.id] = displayTitle;
      if (item.thumbnailUrl) {
        newThumbnails[item.id] = item.thumbnailUrl;
      }

      if (!updated[item.id]) {
        const newMeta: any = {
          id: item.id,
          addedAt: Date.now(),
          addedBy: currentUserId || "system",
          source: "manual",
          displayName: displayTitle,
        };
        if (targetType === "memory") newMeta.memoryId = item.id;
        if (targetType === "media") {
          newMeta.mediaId = item.id;
          if (item.thumbnailUrl) newMeta.thumbnailUrl = item.thumbnailUrl;
        }
        if (targetType === "contact") newMeta.contactId = item.id;
        if (targetType === "group") newMeta.groupId = item.id;
        if (targetType === "place") newMeta.placeId = item.id;
        if (targetType === "anchor") newMeta.anchorId = item.id;

        updated[item.id] = newMeta;

        if (entityId && services) {
          try {
            await linkEntity(services, {
              sourceType: entityType,
              sourceId: entityId,
              targetType: targetType,
              targetId: item.id,
              source: "manual",
            });
          } catch (err: any) {
            console.error(`[EntityLinkStrip] Error linking ${targetType}:`, err);
          }
        }
        onLinkAdded?.(newMeta);
      }
    }

    if (Object.keys(newThumbnails).length > 0) {
      setLocalThumbnails((prev) => ({ ...prev, ...newThumbnails }));
    }
    setLocalTitles((prev) => ({ ...prev, ...newTitles }));
    setLocalLinks(updated);
    onLinksChange?.(updated);
  };

  // If no links and user cannot edit, don't render an empty strip
  if (linkEntries.length === 0 && !canEdit) {
    return null;
  }

  const defaultButtonLabel = buttonLabel || visualConfig.label;

  return (
    <View
      style={[styles.container, compact && styles.containerCompact, style]}
      {...(Platform.OS === "web"
        ? {
            onClick: (e: any) => e?.stopPropagation?.(),
          }
        : {})}
    >
      <View style={styles.flexRow}>
        {linkEntries.map(({ id, meta, title, source, addedBy }) => {
          const canRemove = Boolean(
            canEdit && (!entityId || isOwner || (currentUserId && addedBy === currentUserId) || !addedBy)
          );
          const isItemOwner =
            isOwner || (currentUserId && addedBy === currentUserId) || Boolean(meta?.isOwner);
          const isFiveStar = Boolean(meta?.isFiveStar || meta?.favouriteRating === 5);
          const isFromDescription = source === "description";

          if (targetType === "media") {
            const thumbUrl =
              meta?.thumbnailUrl ||
              meta?.downloadUrl ||
              meta?.url ||
              localThumbnails[id];

            return (
              <View
                key={id}
                style={[
                  styles.mediaCompactWrapper,
                  compact && styles.mediaCompactWrapperCompact,
                ]}
              >
                <ThemedCompactCard
                  entityType="media"
                  id={id}
                  title={title}
                  thumbnailUrl={thumbUrl}
                  isOwner={isItemOwner}
                  isFiveStar={isFiveStar}
                  style={compact ? styles.mediaCardCompactOverride : undefined}
                />

                {canRemove && (
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={(e: any) => {
                      e?.stopPropagation?.();
                      handleRemoveLink(id, meta);
                    }}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    style={styles.mediaRemoveBtn}
                    accessibilityRole="button"
                    accessibilityLabel={`Unlink ${title}`}
                  >
                    <Ionicons name="close" size={12} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            );
          }

          return (
            <ThemedChip
              key={id}
              entityType={targetType}
              id={id}
              label={title}
              isOwner={isItemOwner}
              isFiveStar={isFiveStar}
              compact={compact}
              canRemove={canRemove}
              onRemove={canRemove ? () => handleRemoveLink(id, meta) : undefined}
              style={[
                styles.chipItem,
                compact && styles.chipItemCompact,
              ]}
            />
          );
        })}

        {canEdit && (
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={(e: any) => {
              const coords = extractAnchorCoordinates(e);
              setAnchor(coords);
              e?.stopPropagation?.();
              setModalOpen(true);
            }}
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            style={[
              styles.addButton,
              compact && styles.addButtonCompact,
              targetType === "media" && styles.addMediaButton,
              targetType === "media" && compact && styles.addMediaButtonCompact,
              { backgroundColor: badgeBg, borderColor },
            ]}
          >
            <Ionicons
              name="add"
              size={targetType === "media" ? 22 : compact ? 10 : 11}
              color={tintColor}
            />
            <ThemedText
              style={[
                styles.addText,
                compact && styles.addTextCompact,
                targetType === "media" && styles.addMediaText,
                { color: tintColor },
              ]}
            >
              {defaultButtonLabel}
            </ThemedText>
          </TouchableOpacity>
        )}
      </View>

      {/* Target Type Specific Selector Popovers */}
      {modalOpen && targetType === "memory" && (
        <MemorySelector
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          anchorPosition={anchor}
          selectedMemoryIds={Object.keys(localLinks)}
          onSelectMemory={(mem: MemoryModel) => handleEntitySelected(mem.id!, mem.title)}
          title="Select Memory to Link"
        />
      )}

      {modalOpen && targetType === "media" && (
        <MediaSelector
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          anchorPosition={anchor}
          selectedIds={Object.keys(localLinks)}
          multiSelect={multiSelect}
          onSelectSingleMedia={(m: MediaModel) =>
            handleEntitySelected(
              m.id!,
              m.title || (m as any).name || "Media",
              m.thumbnailUrl || m.url || m.downloadUrl || ""
            )
          }
          onSelectMedia={(mList: MediaModel[]) => {
            handleMultipleEntitiesSelected(
              mList.map((m) => ({
                id: m.id!,
                title: m.title || (m as any).name || "Media",
                thumbnailUrl: m.thumbnailUrl || m.url || m.downloadUrl || "",
              }))
            );
          }}
          title="Select Media to Link"
        />
      )}

      {modalOpen && targetType === "contact" && (
        <ContactSelector
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          anchorPosition={anchor}
          selectedContactIds={Object.keys(localLinks)}
          onSelectContact={(c: ContactModel) =>
            handleEntitySelected(
              c.id || (c as any).contactId,
              c.displayName || (c as any).fullName || (c as any).name || "Contact"
            )
          }
          title="Select Contact to Link"
        />
      )}

      {modalOpen && targetType === "group" && (
        <GroupSelector
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          anchorPosition={anchor}
          selectedGroupIds={Object.keys(localLinks)}
          onSelectGroup={(g: GroupModel) =>
            handleEntitySelected(
              g.id || (g as any).groupId,
              g.name || (g as any).displayName || "Group"
            )
          }
          title="Select Group to Link"
        />
      )}

      {modalOpen && targetType === "place" && (
        <PlaceSelector
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          anchorPosition={anchor}
          selectedPlaceIds={Object.keys(localLinks)}
          onSelectPlace={(p: PlaceModel) =>
            handleEntitySelected(
              p.id,
              p.name || "Place"
            )
          }
          title="Select Place to Link"
        />
      )}

      {modalOpen && targetType === "anchor" && (
        <AnchorSelector
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          anchorPosition={anchor}
          selectedAnchorIds={Object.keys(localLinks)}
          multiSelect={multiSelect}
          onSelectAnchor={(a: AnchorModel) => {
            if (a.id) {
              handleEntitySelected(
                a.id,
                a.name || "Anchor"
              );
            }
          }}
          onSelectMultipleAnchors={(aList: AnchorModel[]) => {
            handleMultipleEntitiesSelected(
              aList.filter((a) => Boolean(a.id)).map((a) => ({
                id: a.id!,
                title: a.name || "Anchor",
              }))
            );
          }}
          title="Select Anchor to Link"
        />
      )}
    </View>
  );
}

export default EntityLinkStrip;

const styles = StyleSheet.create({
  container: {
    marginVertical: 2,
    width: "100%",
  },
  containerCompact: {
    marginVertical: 0,
  },
  flexRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  chipItem: {
    marginRight: 6,
    marginVertical: 1,
  },
  chipItemCompact: {
    marginRight: 4,
    marginVertical: 0,
  },
  chipOuterWrapper: {
    marginRight: 6,
    marginVertical: 1,
  },
  chipOuterWrapperCompact: {
    marginRight: 4,
    marginVertical: 0,
  },
  addButton: {
    height: 19,
    maxHeight: 19,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 5,
    paddingVertical: 0,
    borderRadius: 4,
    borderWidth: 1,
    marginVertical: 1,
    marginRight: 6,
    gap: 2.5,
  },
  addButtonCompact: {
    height: 17,
    maxHeight: 17,
    paddingHorizontal: 4,
    paddingVertical: 0,
    borderRadius: 3,
    marginVertical: 0,
    marginRight: 4,
    gap: 2,
  },
  addText: {
    fontSize: 11,
    lineHeight: 15,
    fontWeight: "600",
  },
  addTextCompact: {
    fontSize: 10,
    lineHeight: 13,
  },
  mediaCompactWrapper: {
    position: "relative",
    marginRight: 8,
    marginBottom: 8,
  },
  mediaCompactWrapperCompact: {
    position: "relative",
    marginRight: 6,
    marginBottom: 6,
  },
  mediaCardCompactOverride: {
    width: 72,
    height: 72,
  },
  mediaRemoveBtn: {
    position: "absolute",
    top: 4,
    left: 4,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  addMediaButton: {
    width: 90,
    height: 90,
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: "dashed",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
    marginBottom: 8,
    marginVertical: 0,
    paddingHorizontal: 6,
    paddingVertical: 6,
    gap: 4,
  },
  addMediaButtonCompact: {
    width: 72,
    height: 72,
    marginRight: 6,
    marginBottom: 6,
  },
  addMediaText: {
    fontSize: 11,
    textAlign: "center",
    marginLeft: 0,
  },
});
